import bowser from 'bowser';
import { keyBy } from 'lodash';
import { createSelector } from 'reselect';
import { DeviceKindNormalized } from '../constants';
import { getDevicesWithCondition, normalizeDevices, calculateDevicesHash, } from '../helpers';
import { getDevicesState } from './devices-state';
const getDevicesWithLabel = (devices) => devices.filter((item) => item.label);
const hasDevices = (devices) => devices.length > 0;
const getDevicesMap = (devices) => keyBy(devices, 'deviceId');
export const selectEnumeratedDevice = (state) => getDevicesState(state).devices;
export const selectVideoAccessability = (state) => getDevicesState(state).hasCameraAccess;
export const selectAudioAccessability = (state) => getDevicesState(state).hasMicrophoneAccess;
export const selectDevices = createSelector(selectEnumeratedDevice, getDevicesWithCondition);
export const selectNormalizedDevices = createSelector(selectDevices, normalizeDevices);
export const selectDevicesHash = createSelector(selectNormalizedDevices, calculateDevicesHash);
export const selectCameras = createSelector(selectNormalizedDevices, (devices) => devices.filter((item) => item.kind === DeviceKindNormalized.videoInput));
export const selectAccessibleCameras = createSelector(selectCameras, getDevicesWithLabel);
/**
 * @description safari 15.4 IOS 13.4 does not recognize video devices before request access
 */
export const selectCameraAvailability = createSelector(selectCameras, (cameras) => hasDevices(cameras) || bowser.safari);
export const selectCamerasAccessability = createSelector(selectAccessibleCameras, hasDevices);
/**
 * @description There is a bug with the camera access control in Safari.
 * If the user denies access to the camera, but allows access to the microphone,
 * Safari still returns the camera in the device list. However, there is no access to it.
 * Therefore, an additional check is carried out with the active video provider.
 * Actual for Safari v15.0.
 */
export const selectCameraAccessability = createSelector(selectCamerasAccessability, selectVideoAccessability, (isCamerasAccessible, isVideoAccessible) => {
    return isVideoAccessible === null
        ? isCamerasAccessible
        : isCamerasAccessible && isVideoAccessible;
});
export const selectCameraOptions = createSelector(selectAccessibleCameras, getDevicesMap);
export const selectMicrophones = createSelector(selectNormalizedDevices, (devices) => devices.filter((item) => item.kind === DeviceKindNormalized.audioInput));
export const selectAccessibleMicrophones = createSelector(selectMicrophones, getDevicesWithLabel);
export const selectMicrophonesAccessability = createSelector(selectAccessibleMicrophones, hasDevices);
export const selectMicrophoneAvailability = createSelector(selectMicrophones, hasDevices);
export const selectMicrophoneAccessability = createSelector(selectMicrophonesAccessability, selectAudioAccessability, (areMicrophonesAvailable, isAudioAccessible) => {
    return isAudioAccessible === null
        ? areMicrophonesAvailable
        : areMicrophonesAvailable && isAudioAccessible;
});
export const selectMicrophoneOptions = createSelector(selectAccessibleMicrophones, getDevicesMap);
export const selectSpeakers = createSelector(selectNormalizedDevices, (devices) => devices.filter((item) => item.kind === DeviceKindNormalized.audioOutput));
export const selectAccessibleSpeakers = createSelector(selectSpeakers, getDevicesWithLabel);
export const selectSpeakerAvailability = createSelector(selectSpeakers, hasDevices);
export const selectSpeakerAccessability = createSelector(selectAccessibleSpeakers, hasDevices);
export const selectSpeakerOptions = createSelector(selectAccessibleSpeakers, getDevicesMap);
export const selectCameraAndMicrophoneAccessability = createSelector(selectCameraAccessability, selectMicrophoneAccessability, (isCameraAccessible, isMicrophoneAccessible) => isCameraAccessible && isMicrophoneAccessible);
export const selectMediaAccessability = createSelector(selectCameraAccessability, selectMicrophoneAccessability, (isCameraAccessible, isMicrophoneAccessible) => isCameraAccessible || isMicrophoneAccessible);
export const selectOnlyMicrophoneAccessible = createSelector(selectCameraAccessability, selectMicrophoneAccessability, (isCameraAccessible, isMicrophoneAccessible) => !isCameraAccessible && isMicrophoneAccessible);
export const selectDeviceUnavailability = createSelector(selectCameraAvailability, selectMicrophoneAvailability, (isCameraAvailable, isMicrophoneAvailable) => !isCameraAvailable && !isMicrophoneAvailable);
export const getAllDevicesBooleanProps = createSelector(selectDeviceUnavailability, selectMediaAccessability, selectCameraAvailability, selectCameraAccessability, selectMicrophoneAvailability, selectMicrophoneAccessability, selectSpeakerAvailability, selectSpeakerAccessability, (areNoDevices, isMediaAccessible, isCameraAvailable, isCameraAccessible, isMicrophoneAvailable, isMicrophoneAccessible, isSpeakerAvailable, isSpeakerAccessible) => ({
    hasNoDevices: areNoDevices,
    hasMediaAccess: isMediaAccessible,
    hasCamera: isCameraAvailable,
    hasCameraAccess: isCameraAccessible,
    hasMicrophone: isMicrophoneAvailable,
    hasMicrophoneAccess: isMicrophoneAccessible,
    hasSpeaker: isSpeakerAvailable,
    hasSpeakerAccess: isSpeakerAccessible,
}));
/**
 * @deprecated use selectEnumeratedDevice() instead
 */
export const getEnumerateDevices = selectEnumeratedDevice;
/**
 * @deprecated use selectVideoAccessability() instead
 */
export const hasVideoAccess = selectVideoAccessability;
/**
 * @deprecated use selectAudioAccessability() instead
 */
export const hasAudioAccess = selectAudioAccessability;
/**
 * @deprecated use selectDevice() instead
 */
export const getDevices = selectDevices;
/**
 * @deprecated use selectNormalizedDevices() instead
 */
export const getNormalizedDevices = selectNormalizedDevices;
/**
 * @deprecated use selectDevicesHash() instead
 */
export const getDevicesHash = selectDevicesHash;
/**
 * @deprecated use selectCameras() instead
 */
export const getCameras = selectCameras;
/**
 * @deprecated use selectAccessibleCameras() instead
 */
export const getAccessibleCameras = selectAccessibleCameras;
/**
 * @deprecated use selectCameraAvailability() instead
 */
export const hasCamera = selectCameraAvailability;
/**
 * @deprecated use selectCamerasAccessability() instead
 */
export const hasAccessibleCameras = selectCamerasAccessability;
/**
 * @deprecated
 */
export const hasCameraAccess = selectCameraAccessability;
/**
 * @deprecated use selectCameraOptions() instead
 */
export const getCameraOptions = selectCameraOptions;
/**
 * @deprecated use selectMicrophones() instead
 */
export const getMicrophones = selectMicrophones;
/**
 * @deprecated use selectAccessibleMicrophones() instead
 */
export const getAccessibleMicrophones = selectAccessibleMicrophones;
/**
 * @deprecated use selectMicrophonesAccessability() instead
 */
export const hasAccessibleMicrophones = selectMicrophonesAccessability;
/**
 * @deprecated use selectMicrophoneAvailability() instead
 */
export const hasMicrophone = selectMicrophoneAvailability;
/**
 * @deprecated use selectMicrophoneAccessability() instead
 */
export const hasMicrophoneAccess = selectMicrophoneAccessability;
/**
 * @deprecated use selectMicrophoneOptions() instead
 */
export const getMicrophoneOptions = selectMicrophoneOptions;
/**
 * @deprecated use selectSpeakers() instead
 */
export const getSpeakers = selectSpeakers;
/**
 * @deprecated use selectAccessibleSpeakers() instead
 */
export const getAccessibleSpeakers = selectAccessibleSpeakers;
/**
 * @deprecated use selectSpeakerAvailability() instead
 */
export const hasSpeaker = selectSpeakerAvailability;
/**
 * @deprecated use selectSpeakerAccessability() instead
 */
export const hasSpeakerAccess = selectSpeakerAccessability;
/**
 * @deprecated use selectSpeakerOptions() instead
 */
export const getSpeakerOptions = selectSpeakerOptions;
/**
 * @deprecated use selectCameraAndMicrophoneAccessability() instead
 */
export const hasCameraAndMicrophoneAccess = selectCameraAndMicrophoneAccessability;
/**
 * @deprecated use selectMediaAccessability() instead
 */
export const hasMediaAccess = selectMediaAccessability;
/**
 * @deprecated use selectOnlyMicrophoneAccessible() instead
 */
export const hasMicrophoneAccessOnly = selectOnlyMicrophoneAccessible;
/**
 * @deprecated use selectDeviceUnavailability() instead
 */
export const hasNoDevices = selectDeviceUnavailability;
